import React, { useState, useEffect, useRef } from 'react';

import { isValidNumberForRegion } from 'libphonenumber-js';
import Router from 'next/router';
import PropTypes from 'prop-types';
import ReCAPTCHA from 'react-google-recaptcha';

import OtpInputField from './otp-input';
import PhoneNumberInput from './phone-number-input';

import { generateLoginOtp, otpLogin } from '../../apis/auth';
import { fetchFeatureEnabledStatus } from '../../apis/flightPath';
import useI18n from '../../hooks/use-i18n';
import { countryDialCodeData } from '../../language.config';
import { PAGEID } from '../../utils/i18nRosseta';
import { validateEmail } from '../../utils/utils';
import Button from '../flight-path/button';
import InputField from '../inputs/inputField';

import '../../styles/flight-path/otp_modal.scss';

export default function OtpLoginModal(props) {
  const { setUserSession } = props;

  const i18n = useI18n();
  const loginPage = i18n.t('login_page');
  const addressDetails = i18n.t('account_address_details');
  const recaptchaRef = useRef();
  const codes = i18n.activeLocale.split('-');
  const country = codes.length === 2 ? codes[0] : 'sg';

  const [retryError, setRetryError] = useState('');
  const [error, setError] = useState('');
  const [isOtpSection, setIsOtpSection] = useState(false);
  const [values, setValues] = useState({
    email: '',
    countryId: country,
    countryCode: country,
    isdCode: countryDialCodeData[country].dialCode,
    phoneNumber: '',
    country: countryDialCodeData[country].name
  });
  const [otpType, setOtpType] = useState('EMAIL');
  const [isGenerateOtpLoading, setIsGenerateOtpLoading] = useState(false);
  const [isSubmitOtpLoading, setIsSubmitOtpLoading] = useState(false);

  const onTypeChangeClick = (type) => {
    setOtpType(type);
  };

  const updateForm = (data) => {
    const buffData = { ...values, ...data };
    setValues(buffData);
    setError(false);
  };

  useEffect(() => {
    setError(false);
  }, [otpType, isOtpSection]);

  const submitGenerateOtp = async () => {
    let userDetails = {};
    if (otpType === 'EMAIL') {
      if (!validateEmail(values.email)) {
        setError(addressDetails.invalid_email);
        return;
      }
      userDetails = { email: values.email };
    } else if (otpType === 'MOBILE') {
      if (
        !isValidNumberForRegion(
          values.phoneNumber,
          values.countryCode.toUpperCase()
        )
      ) {
        setError(addressDetails.invalid_phone_number);
        return;
      }
      userDetails = { phoneNumber: values.phoneNumber, isdCode: values.isdCode };
    }
    setIsGenerateOtpLoading(true);
    const captchaToken = process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_INVISIBLE
      ? await recaptchaRef.current.executeAsync()
      : ' ';
    const otpRes = await generateLoginOtp({
      ...userDetails,
      type: otpType,
      captchaToken,
      locale: i18n.activeLocale
    });
    if (otpRes?.data?.errors?.errorCode) {
      let errorMsg = loginPage.sign_up_errors[otpRes.data.errors?.errorCode]
        || loginPage.login_otp_error;
      if (otpRes.data.errors?.errorCode === 'ERROR_FOUND') errorMsg = loginPage.sign_up_errors.TOO_MANY_REQUESTS;
      setError(errorMsg);
    } else if (otpRes?.message === 'OK') {
      setIsOtpSection(true);
      setError(false);
    } else {
      setError(loginPage.login_otp_error);
    }
    if (process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_INVISIBLE) {
      recaptchaRef.current.reset();
    }
    setIsGenerateOtpLoading(false);
  };

  const onOtpSubmit = async (otp) => {
    setIsSubmitOtpLoading(true);
    setError(false);
    const loginRes = await otpLogin({
      email: values.email,
      phoneNumber: values.phoneNumber,
      isdCode: values.isdCode,
      type: otpType,
      otp,
      fallback: false,
      countryId: country.toUpperCase(),
      source: PAGEID.FLIGHT_PATH
    });
    if (loginRes?.data !== null && loginRes?.errors === null) {
      const isEnabled = await fetchFeatureEnabledStatus();
      if (isEnabled.is_feature_enabled) {
        setUserSession(loginRes.data.userDetail);
        const isUserFirstLogin = localStorage.getItem('isUserFirstLogin');
        if (!isUserFirstLogin) localStorage.setItem('isUserFirstLogin', true);
      } else {
        Router.push(`/${i18n.activeLocale}/flight-path/404`);
      }
    } else {
      const errorMsg = loginPage.sign_up_errors[loginRes?.data?.errors?.errorCode]
        || loginPage.signup_otp_error;
      if (loginRes.data.errors?.errorCode === 'TOO_MANY_REQUESTS') setRetryError(errorMsg);
      else setError(errorMsg);
    }
    setIsSubmitOtpLoading(false);
  };

  return (
    <div className="otp-modal-wrapper login-modal">
      <div className="modal-overlay">
        <div className="modal-container text-center">
          <div className="content-area">
            {otpType === 'EMAIL' ? (
              <img src="/img/icon-OTP-email.svg" alt="email icon" />
            ) : (
              <img src="/img/icon-OTP-mobile.svg" alt="mobile icon" />
            )}
            {isOtpSection ? (
              <OtpInputField
                values={values}
                onSubmit={onOtpSubmit}
                error={error}
                otpType={otpType}
                sectionChange={setIsOtpSection}
                retryError={retryError}
                setRetryError={setRetryError}
                resendOtp={submitGenerateOtp}
                isSubmitOtpLoading={isSubmitOtpLoading}
              />
            ) : (
              <div className="user-input-area">
                {otpType === 'EMAIL' && (
                  <div className="signup-input-wrapper">
                    <InputField
                      type="text"
                      name="email"
                      label={loginPage.email}
                      value={values.email}
                      onChange={updateForm}
                      autoComplete="email"
                      warning={error}
                    />
                    {error && <span className="input-error">{error}</span>}
                  </div>
                )}
                {otpType === 'MOBILE' && (
                  <div>
                    <PhoneNumberInput
                      values={values}
                      updateForm={updateForm}
                      error={error}
                    />
                  </div>
                )}
                <div className="d-flex flex-column align-items-center">
                  <Button
                    onClick={submitGenerateOtp}
                    isLoading={isGenerateOtpLoading}
                  >
                    {loginPage.submit}
                  </Button>
                  <a
                    className="otp-change d-block"
                    role="presentation"
                    onClick={() => onTypeChangeClick(
                      otpType === 'EMAIL' ? 'MOBILE' : 'EMAIL'
                    )}
                  >
                    {otpType === 'EMAIL'
                      ? loginPage.sent_otp_mobile
                      : loginPage.sent_otp_email}
                  </a>
                </div>
              </div>
            )}
            {process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_INVISIBLE && (
              <div className="captcha-container mt-4">
                <ReCAPTCHA
                  sitekey={process.env.NEXT_PUBLIC_RECAPTCHA_SITE_KEY_INVISIBLE}
                  size="invisible"
                  ref={recaptchaRef}
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

OtpLoginModal.propTypes = {
  setUserSession: PropTypes.func
};

OtpLoginModal.defaultProps = {
  setUserSession: () => {}
};
